import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Activity,
  Bell,
  FileText,
  Map as MapIcon,
  Menu,
  Settings as SettingsIcon,
  ShieldCheck,
  Sliders,
  Truck,
  Wrench,
} from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { HeaderStats } from './HeaderStats';
import { WeatherWidget } from './WeatherWidget';
import { HeaderProfile } from './HeaderProfile';
import { cn } from '@/lib/utils';
import { useFleet } from '@/hooks/useFleet';
import { useTraccarSocket } from '@/hooks/useTraccarSocket';
import { useGeofenceAutoBlock } from '@/hooks/useGeofenceAutoBlock';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useEventsCollector } from '@/hooks/useEventsCollector';
import { useCommandResultListener } from '@/hooks/useCommandResultListener';
import { useEventsStore } from '@/stores/events';
import { NotificationToggle } from './NotificationToggle';
import { HeaderNotifications } from './HeaderNotifications';
import { SoundToggle } from './SoundToggle';

const primaryNav = [
  { to: '/devices', label: 'Máquinas', icon: Truck },
  { to: '/map', label: 'Mapa', icon: MapIcon },
  { to: '/dashboard', label: 'Dashboard', icon: Activity },
];

const secondaryNav = [
  { to: '/reports', label: 'Relatórios', icon: FileText },
  { to: '/geofences', label: 'Geocercas', icon: ShieldCheck },
  { to: '/maintenance', label: 'Manutenção', icon: Wrench },
  { to: '/notifications', label: 'Notificações', icon: Bell },
  { to: '/settings', label: 'Configurações', icon: SettingsIcon },
  { to: '/profile', label: 'Perfil', icon: Sliders },
];

export function AppLayout() {
  useFleet();
  useTraccarSocket();
  useGeofenceAutoBlock();
  usePushNotifications();
  useEventsCollector();
  useCommandResultListener();

  const [sidebarHidden, setSidebarHidden] = useState(false);
  const unreadCount = useEventsStore((s) => s.items.filter((e) => !e.read).length);

  return (
    <div className="flex h-full">
      <aside
        className={cn(
          'shrink-0 flex-col border-r border-border bg-card transition-all duration-200',
          sidebarHidden ? 'hidden lg:hidden' : 'hidden w-60 lg:flex',
        )}
      >
        <div className="flex h-16 items-center gap-3 px-4">
          <img src="/fismatek-icon.png" alt="" className="h-11 w-11" />
          <span className="text-lg font-bold tracking-tight">Fismatek</span>
        </div>
        <nav className="flex-1 space-y-6 overflow-y-auto p-3">
          <div className="space-y-1">
            {primaryNav.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>
          <div className="space-y-1">
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Mais
            </p>
            {secondaryNav.map((item) => (
              <NavItem
                key={item.to}
                {...item}
                badge={item.to === '/notifications' ? unreadCount : undefined}
              />
            ))}
          </div>
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative flex h-20 items-center justify-between gap-3 border-b border-border bg-card px-3 lg:px-4">
          <div className="flex flex-1 items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarHidden((v) => !v)}
              className="hidden h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground lg:inline-flex"
              title={sidebarHidden ? 'Mostrar menu' : 'Esconder menu'}
              aria-label="Alternar menu lateral"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 lg:hidden">
              <img src="/fismatek-icon.png" alt="" className="h-9 w-9" />
              <span className="text-lg font-bold">Fismatek</span>
            </div>
            <div className="hidden lg:block xl:hidden">
              <HeaderStats dense />
            </div>
            <div className="hidden xl:block">
              <HeaderStats />
            </div>
          </div>

          {sidebarHidden && (
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 lg:block">
              <img
                src="/fismatek-logo.png"
                alt="Fismatek"
                className="h-16 object-contain"
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <HeaderNotifications />
            <SoundToggle />
            <NotificationToggle />
            <WeatherWidget />
            <HeaderProfile />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 overflow-auto pb-20 lg:pb-0">
          <Outlet />
        </main>
        <MobileBottomNav />
      </div>
    </div>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  badge,
}: {
  to: string;
  label: string;
  icon: typeof Truck;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {badge && badge > 0 ? (
        <span className="rounded-full bg-status-blocked px-1.5 text-[10px] font-bold text-destructive-foreground">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </NavLink>
  );
}

function MobileBottomNav() {
  const items = [
    primaryNav[0],
    primaryNav[1],
    primaryNav[2],
    { to: '/profile', label: 'Perfil', icon: Sliders },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-4 border-t border-border bg-card lg:hidden">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            cn(
              'flex flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )
          }
        >
          <item.icon className="h-5 w-5" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}
